import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowUpRight, Star, GraduationCap, ShieldCheck, Sparkles, Camera, HeartHandshake, Leaf } from "lucide-react";
import { COMPANY, SERVICES, WHY_CHOOSE, PROCESS, IMAGES, AREAS } from "@/data/site";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { trackCallConversion } from "@/lib/analytics";

const ICONS = { GraduationCap, ShieldCheck, Sparkles, Camera, HeartHandshake, Leaf };

export default function Home() {
  useSeo(
    "Exterior Cleaning Lancashire | StoneCrest Exterior Specialists",
    "Professional exterior cleaning across Lancashire including roof cleaning, render softwashing, driveway cleaning, gutter clearing and fascia cleaning. Fully insured with free fixed-price quotes.",
    "https://stonecrestexteriors.co.uk/"
  );
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.12, 1.28]);

  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setRating(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
          <img src={IMAGES.heroDusk} alt="Premium UK home at dusk with warm lighting" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220]/80 via-transparent to-[#0b1220]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220]/55 via-transparent to-transparent" />

        <div className="relative h-full px-6 md:px-12 max-w-7xl mx-auto flex flex-col justify-end pb-20 md:pb-28">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} className="flex items-center gap-3 mb-6">
            <div className="flex text-[var(--oxblood)]">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4" fill="currentColor" />)}</div>
            <span className="text-sm text-white/85 tracking-wide">Rated 5.0 by Lancashire homeowners</span>
          </motion.div>

          <h1 className="font-serif font-light text-white text-[14vw] sm:text-7xl lg:text-[7rem] leading-[0.92] tracking-tight max-w-4xl">
            <MaskedLines lines={["Professional Exterior"]} />
            <span className="reveal-mask">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}>
                Cleaning in Lancashire<span className="text-[var(--oxblood)]">.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-7 text-white/80 max-w-2xl text-base md:text-lg">
            Premium — Roof Cleaning &nbsp;•&nbsp; Render Softwashing &nbsp;•&nbsp; Driveway Cleaning &nbsp;•&nbsp; Gutter Clearing &nbsp;•&nbsp; Fascia &amp; Soffit Cleaning
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/quote" data-testid="hero-quote-btn" className="group bg-[var(--oxblood)] text-white px-9 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors flex items-center justify-center gap-2">
              Get a Free Quote <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <a href={COMPANY.phoneHref} onClick={trackCallConversion} data-testid="hero-call-btn" className="border border-white/50 text-white px-9 py-4 rounded-full tracking-wide hover:bg-white hover:text-[var(--ink)] transition-all flex items-center justify-center gap-2 backdrop-blur-sm">
              <Phone className="w-4 h-4" /> Call Now
            </a>
          </motion.div>
        </div>
      </section>

      <Marquee items={SERVICES.map((s) => s.name)} />

      {/* WHY CHOOSE US */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto py-24 md:py-32" data-testid="why-choose-section">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Why StoneCrest</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">A higher standard of exterior care.</h2></Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <Reveal delay={0.1}><p className="text-[var(--muted)]">Professional exterior cleaning for residential and commercial properties across Lancashire — from homes and managed lettings to offices, shops and retail premises. Everything we do is built around premium equipment, careful insured methods and a spotless finish.</p></Reveal>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
          {WHY_CHOOSE.map((item, i) => {
            const Icon = ICONS[item.icon];
            return (
              <Reveal key={item.title} delay={i * 0.06} className="bg-white p-9 group hover:bg-[var(--bone)] transition-colors duration-500" data-testid={`why-card-${i}`}>
                <Icon className="w-8 h-8 text-[var(--oxblood)] mb-6" strokeWidth={1.3} />
                <h3 className="font-serif text-2xl mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--muted)] leading-relaxed">{item.text}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[var(--bone)]" data-testid="services-section">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Our Services</p></Reveal>
              <Reveal delay={0.05}><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight max-w-xl">Five specialisms. One flawless finish.</h2></Reveal>
            </div>
            <Reveal delay={0.1}><Link to="/services" className="text-sm tracking-wide border-b border-[var(--ink)] pb-1 hover:text-[var(--oxblood)] hover:border-[var(--oxblood)] transition-colors">View all services</Link></Reveal>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group block bg-white border border-transparent hover:border-[var(--border)] transition-all duration-500 h-full">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-display">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl md:text-3xl mb-2 group-hover:text-[var(--oxblood)] transition-colors">{s.name}</h3>
                    <p className="text-sm text-[var(--muted)] mb-5">{s.tagline}</p>
                    <span className="text-xs uppercase tracking-[0.2em] flex items-center gap-2 text-[var(--oxblood)]">Explore <ArrowUpRight className="w-3.5 h-3.5" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS MANIFESTO */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto py-24 md:py-32" data-testid="process-section">
        <div className="mb-16 max-w-2xl">
          <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Our Process</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-serif font-light text-4xl md:text-6xl leading-tight">From first call to spotless finish.</h2></Reveal>
        </div>
        <div>
          {PROCESS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.04}>
              <div className="grid md:grid-cols-12 gap-4 md:gap-8 items-baseline border-t border-[var(--border)] py-8 group hover:bg-[var(--bone)]/60 transition-colors duration-500 px-2">
                <span className="md:col-span-2 font-display text-4xl md:text-5xl text-[var(--oxblood)]/80 group-hover:text-[var(--oxblood)] transition-colors">{step.n}</span>
                <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl">{step.title}</h3>
                <p className="md:col-span-6 text-[var(--muted)]">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REVIEWS PREVIEW */}
      <section className="bg-[var(--ink)] text-white" data-testid="reviews-preview">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Customer Reviews</p>
              <h2 className="font-serif font-light text-4xl md:text-6xl leading-tight">Trusted by Lancashire homeowners.</h2>
            </div>
            {rating && rating.review_count > 0 && (
              <div className="text-right">
                <div className="font-serif text-6xl">{rating.average_rating.toFixed(1)}</div>
                <div className="flex text-[var(--oxblood)] justify-end my-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4" fill="currentColor" />)}</div>
                <span className="text-xs text-white/50">{rating.review_count} reviews</span>
              </div>
            )}
          </div>
          {reviews.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.map((r) => (
                <div key={r.id} className="border border-white/15 p-8">
                  <div className="flex text-[var(--oxblood)] mb-4">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-4 h-4" fill="currentColor" />)}</div>
                  <p className="font-serif text-xl leading-relaxed text-white/90 mb-6">"{r.text}"</p>
                  <p className="text-sm text-white/50">{r.author}{r.location ? ` · ${r.location}` : ""}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/50 font-serif text-2xl italic">Genuine customer reviews will appear here as our first customers share their experience.</p>
          )}
          <div className="mt-12">
            <Link to="/reviews" className="text-sm tracking-wide border-b border-white pb-1 hover:text-[var(--oxblood)] hover:border-[var(--oxblood)] transition-colors">Read all reviews</Link>
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto py-24 md:py-32" data-testid="areas-strip">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <Reveal><p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Areas We Cover</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">Proudly serving {COMPANY.region}.</h2></Reveal>
            <Reveal delay={0.1}><Link to="/areas" className="inline-block mt-6 text-sm tracking-wide border-b border-[var(--ink)] pb-1 hover:text-[var(--oxblood)] transition-colors">All areas</Link></Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)]">
            {AREAS.map((a, i) => (
              <Reveal key={a} delay={i * 0.05}>
                <Link to={`/areas/${a.toLowerCase()}`} className="block bg-white px-7 py-6 group hover:bg-[var(--oxblood)] hover:text-white transition-colors duration-400" data-testid={`area-link-${a.toLowerCase()}`}>
                  <span className="font-serif text-2xl flex items-center justify-between">{a} <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection sub="Understand exactly what your property needs and request a fixed-price quote in under a minute." />
    </div>
  );
}
